<template>
  <div v-if="showComponent" id="lm-participants">
    <div class="row">
      <div class="col-12">
        <h4 v-if="title !== null"> {{title}} </h4>
        <h4 v-else> {{$t('lm_participants_title')}} </h4>
        <p v-if="subtitle !== null"> {{subtitle}} </p>
        <p v-else> {{$t('lm_participants_subtitle')}} </p>
      </div>
      <b-col v-if="showWorkflowChangeSection" cols="12" md="6" class="action-section">
        <LmTreeSelect
          :value="signers"
          @input="onSelectParticipant"
          :options="participantsOptions"
          :label="label"
          :disabled="disabled"
          :showCount="true"
          :disableBranchNodes="true"
          multiple
        />
        <b-button
          class="add-external-participant-btn"
          size="sm"
          variant="info"
          v-b-modal="`externalSignerModal_${participantIndex}`"
          v-if="enableExternalSigners"
          :disabled="disabled"
        >
          <font-awesome-icon icon="plus" class="mr-2"></font-awesome-icon>
          <span class="d-none d-sm-inline">Participante</span>
          <i class="d-sm-none d-inline mdi mdi-account-group mr-1"></i>
          Externo
        </b-button>
        <b-button
          class="add-external-participant-btn"
          size="sm"
          variant="info"
          v-b-modal="`inpersonSignerModal_${participantIndex}`"
          v-if="allowInPersonParticipant"
          :disabled="disabled"
        >
          <font-awesome-icon icon="plus" class="mr-2"></font-awesome-icon>
          <span class="d-none d-sm-inline">Firmante</span>
          <i class="d-sm-none d-inline mdi mdi-grease-pencil mr-1"></i> Presencial
        </b-button>
      </b-col>
      <b-col cols="12" :md="workflowVisualSectionCols" class="workflow-section"           :class="!showWorkflowChangeSection ? 'disabled' : ''">
        <draggable
          v-if="signers.length"
          tag="b-list-group"
          v-model="signers"
          @start="drag=true"
          @end="drag=false"
          animation="150"
          :disabled="disabled"
        >
          <b-list-group-item
            v-for="(item, index) in signers"
            :key="index"
            class="d-flex justify-content-between align-items-center handle"
            :class="{ 'border border-danger': error && index === signers.length - 1 }"
            >
            <div class="d-flex align-items-center">
              <span class="participants-order mr-2">{{ index + 1 }}</span>
              {{ item.name }}
            </div>
            <div class="d-flex justify-content-end align-items-center">
              <div>
                <b-dropdown variant="text" boundary="viewport" :disabled="!canAddReviewers || disabled || item.userType === 'inperson' || workflowItemIsReviewerGroup(item)">
                  <template #button-content>
                    <i class="mdi mr-1" :class="item.action === 'S' ? 'mdi-grease-pencil' : 'mdi-eye-outline'"></i>
                    <span class="d-none d-sm-inline">
                    {{ item.action === 'S' ? 'Firma' : 'Revisa' }}
                    </span>
                  </template>
                  <b-dropdown-item-button @click="item.action = 'S'">
                    <i class="mdi mdi-grease-pencil mr-1"></i>
                    Firma
                  </b-dropdown-item-button>
                  <b-dropdown-item-button @click="item.action = 'R'" v-if="canAddReviewers">
                    <i class="mdi mdi-eye-outline mr-1"></i>
                    Revisa
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>
            </div>
          </b-list-group-item>
        </draggable>
        <div v-else>
          <LmSvgIcon
            icon="LmNoDataIcon"
            width="7.5em"
            wrapperClass="mb-4 d-flex justify-content-center"
          />
          <p class="text-center">
            Todavía no se han seleccionado participantes para este documento.
          </p>
        </div>
        <p class="text-danger text-italic mt-2" v-if="error">
          {{ errorMessage }}
        </p>
      </b-col>
    </div>
    <b-modal
      :id="`inpersonSignerModal_${participantIndex}`"
      centered
      title="Agregar un firmante presencial"
      cancel-title="Cancelar"
      ok-title="Agregar"
      size="lg"
      @ok="onAppendInPerson"
      @cancel="resetInPerson"
      @hidden="resetInPerson"
    >
      <b-row>
        <LmEmailInput
          class="col-md-8 col-12"
          placeholder="Email"
          label="Email"
          v-model="$v.inPerson.email.$model"
          :v="val('inPerson.email')"
        />
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="En carácter de"
          label="En carácter de"
          :v="val('inPerson.inCharacterOf')"
          v-model="inPerson.inCharacterOf"
        />
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Nombre"
          label="Nombre"
          v-model="$v.inPerson.firstname.$model"
          :v="val('inPerson.firstname')"
        />
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Apellido"
          label="Apellido"
          v-model="$v.inPerson.lastname.$model"
          :v="val('inPerson.lastname')"
        />
        <LmNumberInputV2
          class="col-md-4 col-12"
          placeholder="DNI"
          label="DNI"
          v-model="inPerson.dni"
        />
      </b-row>
    </b-modal>
    <b-modal
      :id="`externalSignerModal_${participantIndex}`"
      centered
      title="Agregar un participante externo"
      cancel-title="Cancelar"
      ok-title="Agregar"
      size="lg"
      @ok="onAddExternalSigner"
      @cancel="resetExternalSigner"
      @hidden="resetExternalSigner"
      v-if="enableExternalSigners"
    >
      <b-row>
        <div class="col-12 d-flex align-items-end">
          <LmEmailInput
            class="w-100"
            placeholder="Email"
            label="Email"
            v-model="$v.externalSigner.email.$model"
            :v="val('externalSigner.email')"
          />
          <LmButton
            class="ml-2"
            @click="onSearchSignerData"
            buttonClass="btn-primary"
            value="Buscar usuario"
            :disabled="!externalSigner.email"
          />
        </div>
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Nombre"
          label="Nombre"
          v-model="$v.externalSigner.firstname.$model"
          :v="val('externalSigner.firstname')"
          :disabled="needToSearch"
        />
        <LmTextInput
          class="col-md-4 col-12"
          placeholder="Apellido"
          label="Apellido"
          v-model="$v.externalSigner.lastname.$model"
          :v="val('externalSigner.lastname')"
          :disabled="needToSearch"
        />
        <LmNumberInputV2
          class="col-md-4 col-12"
          placeholder="DNI"
          label="DNI"
          v-model="$v.externalSigner.dni.$model"
          :v="val('externalSigner.dni')"
          :disabled="needToSearch"
        />
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'

import draggable from 'vuedraggable'
import _ from 'lodash'
import {
  required,
  email
} from 'vuelidate/lib/validators'
import commonsApi from '@/api/commons'

import lmInputMixin from '@/mixins/lmInputMixin.js'
import lmToasts from '@/mixins/lmToasts.js'

export default {
  mixins: [lmInputMixin, lmToasts],
  components: {
    LmTreeSelect,
    LmButton,
    LmTextInput,
    LmNumberInputV2,
    draggable,
    LmEmailInput
  },
  props: {
    optionsPath: {
      type: String,
      default: '/users/all'
    },
    canAddReviewers: {
      type: Boolean,
      default: true
    },
    canAddExternalParticipants: {
      type: Boolean,
      default: true
    },
    allowInPersonParticipant: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    showWorkflowChangeSection: {
      type: Boolean,
      default: true
    },
    reviewersGroupsPath: {
      type: String,
      default: '/customer/reviewersgroups'
    },
    showReviewersGroups: {
      type: Boolean,
      default: false
    },
    participantIndex: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      error: false,
      errorMessage: '',
      needToSearch: true,
      participantsOptions: null,
      externalSigner: {
        firstname: '',
        lastname: '',
        email: '',
        dni: '',
        userType: 'external',
        action: 'S'
      },
      inPerson: {
        id: '',
        firstname: '',
        lastname: '',
        dni: '',
        email: '',
        inCharacterOf: ''
      }
    }
  },
  validations: {
    externalSigner: {
      firstname: {
        required
      },
      lastname: {
        required
      },
      email: {
        email,
        required
      },
      dni: {
        required
      }
    },
    inPerson: {
      firstname: {
        required
      },
      lastname: {
        required
      },
      email: {
        email
      }
    }
  },
  computed: {
    signers: {
      get () {
        return this.value || null
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    enableExternalSigners () {
      return process.env?.VUE_APP_ENABLE_EXTERNAL_SIGNERS === 'yes' && this.canAddExternalParticipants
    },
    workflowVisualSectionCols () {
      return this.showWorkflowChangeSection ? 6 : 12
    }
  },
  methods: {
    val (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    onAppendInPerson () {
      const user = {
        ...this.inPerson,
        id: Date.now(),
        name: `${this.inPerson.firstname} ${this.inPerson.lastname} - Presencial`,
        action: 'S',
        userType: 'inperson'
      }
      this.signers.push(user)
      this.resetInPerson()
      this.$bvModal.hide(`inpersonSignerModal_${this.participantIndex}`)
    },
    resetInPerson () {
      for (const key in this.inPerson) {
        this.inPerson[key] = ''
      }
      this.$v.$reset()
    },
    onAddExternalSigner () {
      if (this.signers.some(el => el.id === this.externalSigner?.id)) return
      this.signers.push({
        id: Date.now(),
        ...this.externalSigner,
        name: `${this.externalSigner.firstname} ${this.externalSigner.lastname} - Externo`
      })
      this.resetExternalSigner()
      this.$v.$reset()
    },
    async onSearchSignerData () {
      try {
        this.needToSearch = true
        const user = await commonsApi.axiosGetWrapper(`/users/find-external-user/${this.externalSigner.email}`)
        if (user.data) {
          if (user.data?.userType === 'external') {
            this.externalSigner.firstname = user.data.firstname
            this.externalSigner.lastname = user.data.lastname
            this.externalSigner.dni = user.data.dni
            this.externalSigner.id = user.data._id
            this.needToSearch = true
          } else if (user.data?.userType === 'internal') {
            if (this.signers.some(el => el.id === user.data._id)) {
              this.lmToastDangerMessage('El usuario ya se encuentra como participante.', 'Error')
              throw new Error('El usuario ya se encuentra como participante.')
            }
            this.signers.push({
              id: user.data._id,
              name: `${user.data.firstname} ${user.data.lastname}`,
              action: 'S'
            })
            this.resetExternalSigner()
            this.$v.$reset()
            this.$bvModal.hide(`externalSignerModal_${this.participantIndex}`)
          }
        } else {
          this.externalSigner.firstname = ''
          this.externalSigner.lastname = ''
          this.externalSigner.dni = ''
          this.needToSearch = false
        }
      } catch (error) {
        console.log(error)
      }
    },
    resetExternalSigner () {
      this.externalSigner.firstname = ''
      this.externalSigner.lastname = ''
      this.externalSigner.dni = ''
      this.externalSigner.email = ''
      this.$v.$reset()
    },
    // Esto es importante para que no se reseteen las opciones de Firmante o Visador
    // Al cambiar el orden de los participantes (treeselect re emite los items desde las opciones con "S" por defecto)
    onSelectParticipant (event) {
      if (event.length) {
        event.forEach(el => {
          const selected = this.signers.find(s => s.id === el.id)
          if (selected) {
            el.action = selected.action
          }
        })
      }
      this.$emit('input', event)
    },
    workflowItemIsReviewerGroup (item) {
      return item?.type === 'RG'
    }
  },
  async created () {
    try {
      const options = await commonsApi.axiosGetWrapper(this.optionsPath)
      const participants = options.data.items.map(opt => {
        return {
          id: opt.id,
          name: opt.name,
          action: 'S'
        }
      })

      if (this.showReviewersGroups) {
        const reviewersGroupsOptions = await commonsApi.axiosGetWrapper(this.reviewersGroupsPath)
        const reviewersGroups = reviewersGroupsOptions.data.items.map(opt => {
          return {
            id: opt.id,
            name: opt.name,
            action: 'R',
            type: 'RG'
          }
        })
        this.participantsOptions = [
          {
            id: 'participantes',
            name: 'Usuarios',
            children: participants
          },
          {
            id: 'gruposrevisores',
            name: 'Grupos de Revisores',
            children: reviewersGroups
          }
        ]
      } else {
        this.participantsOptions = participants
      }
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style lang="sass">
#lm-participants
  .participants-order
    width: 25px
    height: 25px
    border: 1px solid gray
    border-radius: 50%
    display: flex
    justify-content: center
    align-items: center
  .btn.dropdown-toggle:focus, .add-external-participant-btn:focus
    outline: none
    box-shadow: none
  .btn
    margin: 1px
    border-radius: 0.2rem
  .workflow-section.disabled
    .list-group-item
      cursor: initial
</style>
